// Player Goals/Cards - Movement — one player's odds drift over time. Thin
// wrapper over <MovementChart> (the shared D3 line-chart primitive), one
// component for both titles via a `kind` variant.
//
// Phase 4: the `.player-toggle` is now a React control owned by <ModuleControls>
// (options derived from fixture+scope+marketType via `playerOptionsFor`, the same
// helper used here). So this widget no longer populates the shell toggle — it
// just reads `player` from the store (`ctx.player`) and draws, clamping to the
// in-scope default via `selectedPlayerFor` (the store value can lag a scope/type
// change until the control re-renders). Scope filters the player LIST offered by
// the control, not the drawn series (keyed fixture → market type → player).
//
// M2 wave 10: the drawn SERIES + offsets now arrive from app-api via
// `useWidgetData` as one `PlayerMovementPayload` envelope `{ series, bkOffsets }`,
// keyed by variant (`player-goals-movement` | `player-cards-movement`; both the
// same payload type, so no per-branch cast). The player LIST still comes from
// `selectedPlayerFor` → `playerControlOptions` (legacy `getPlayer*MovementData`) —
// a control concern that stays legacy, so those two getters are NOT deleted.

import { useRef } from "react";
import { shapeMovement, playerMovementLabel } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import MovementChart from "./MovementChart.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";
import { selectedPlayerFor } from "../../data/playerControlOptions.js";

export interface PlayerMovementProps {
  kind: "goals" | "cards";
  fixture: string;
  bookmaker?: string | null;
  scope?: string | null;
  /** Market type from `mod.dataset.marketType`: "Anytime" | "First" | "2+". */
  marketType?: string | null;
  /** Player toggle value; empty/null on first mount (toggle not yet populated). */
  player?: string | null;
}

export default function PlayerMovement({
  kind,
  fixture,
  bookmaker,
  scope,
  marketType,
  player,
}: PlayerMovementProps) {
  const fix = fixture || "Arsenal v Everton";
  const mtype = marketType || "Anytime";
  const sc = scope || "Match";
  const wrapperClass = kind === "goals" ? "pgm-wrapper" : "pcm-wrapper";

  // Both variant keys return PlayerMovementPayload, so no per-branch cast.
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { data, isPending, isError, refetch } = useWidgetData(
    kind === "goals" ? "player-goals-movement" : "player-cards-movement",
    useFixtureKey(fix)
  );
  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  const movData = data?.series ?? {};
  const fixMov = movData[fix] ?? movData["Arsenal v Everton"];
  const allTypeData = fixMov?.[mtype] ?? fixMov?.["Anytime"] ?? {};

  // The control (<ModuleControls>) owns the toggle + its options; here we only
  // clamp the stored `player` to the in-scope default via the shared helper.
  const selected = selectedPlayerFor(kind, fix, sc, mtype, player);

  // `selected` is always a key of allTypeData when the list is non-empty (it
  // comes from Object.keys), so this resolves the series directly; `[]` only
  // when there are no players → empty wrapper (matching the legacy `!base` bail).
  const base = allTypeData[selected] ?? [];
  const offset = (data?.bkOffsets ?? {})[bookmaker || "Pinnacle"] ?? 0;
  const label = playerMovementLabel(selected, mtype, kind);
  const shape = shapeMovement(base, offset, label);
  if (!shape)
    return <div className={`${wrapperClass} bm-wrapper`} ref={wrapperRef} />;
  return (
    <MovementChart wrapperClass={wrapperClass} shape={shape} rootRef={wrapperRef} />
  );
}
